import { createAction as makeActionCreator } from 'redux-actions';
import { map, includes } from 'lodash/collection';
import { mapValues } from 'lodash/object';
import ReactGA from 'react-ga';
import _ from 'lodash';
import ls from 'local-storage';

import api from '../api/';
import { handleError } from './errors';
import { setCreateUserIfNotExists, setRequiresEmployer, setSelectMainEmployer, setIdentityUserFailureDueToMultipleEmployers } from './session';
import { loadPathNamespace } from './app';

import SelectionHandler from '../util/SelectionHandler';
import { fetchThroughHashedApi, localizeUnitName, unitsReceived } from './ehrResources';
import { closeCancelReservationScreen } from './cancelling';

export const rootLoading = makeActionCreator('ROOT_CONFIG_LOADING');
export const rootLoaded = makeActionCreator('ROOT_CONFIG_LOADED');
export const pathLoading = makeActionCreator('PATH_CONFIG_LOADING');
export const setFetchPathAfterLogin = makeActionCreator('FETCH_PATH_AFTER_LOGIN');
export const setSelectedPath = makeActionCreator('PATH_SELECTED');
export const pathLoaded = makeActionCreator('PATH_LOADED');
export const selectedOptionsChanged = makeActionCreator('SELECTED_OPTIONS_CHANGED');
export const additionalDiscussionChanged = makeActionCreator('ADDITIONAL_DISCUSSION_CHANGED');
export const requestingTimeSlots = makeActionCreator('REQUESTING_TIME_SLOTS');
export const timeSlotsReceived = makeActionCreator('TIME_SLOTS_RECEIVED');
export const clearTimeSlots = makeActionCreator('CLEAR_TIME_SLOTS');
export const storingReservation = makeActionCreator('STORING_RESERVATION');
export const reservationStored = makeActionCreator('RESERVATION_STORED');
export const resetBooking = makeActionCreator('RESET_BOOKING');
export const setFetchTimesPermissions = makeActionCreator('SET_FETCH_TIMES_PERMISSIONS');
export const setLoginFirst = makeActionCreator('SET_LOGIN_FIRST');
export const setLoginFirstShowLoginForm = makeActionCreator('SET_LOGIN_FIRST_SHOW_LOGIN_FORM');
export const setPathNamespace = makeActionCreator('SET_PATH_NAMESPACE');
export const setPathReservationByCodeEnabled = makeActionCreator('SET_PATH_RESERVATION_BY_CODE_ENABLED');
export const setUtilizeEmployerInformation = makeActionCreator('SET_UTILIZE_EMPLOYER_INFORMATION');
export const setSelectionHandler = makeActionCreator('SET_SELECTION_HANDLER');
export const setSelectedPathType = makeActionCreator('SET_PATH_TYPE');
export const setRequireSingleEmployer = makeActionCreator('SET_REQUIRE_SINGLE_EMPLOYER');
export const selectManagementOption = makeActionCreator('SELECT_MANAGEMENT_OPTION');
export const selectManagementOptionWithSinglePath = makeActionCreator('SELECT_MANAGEMENT_OPTION_WITH_SINGLE_PATH');
export const unselectManagementOptionWithSinglePath = makeActionCreator('UNSELECT_MANAGEMENT_OPTION_WITH_SINGLE_PATH');
export const setLegalEntity = makeActionCreator('SET_LEGAL_ENTITY');
export const openPromptEmployerSelection = makeActionCreator('SET_PROMPT_EMPLOYER_SELECTION');
export const closePromptEmployerSelection = makeActionCreator('CLOSE_PROMPT_EMPLOYER_SELECTION');
export const setPathReservationReasonCode = makeActionCreator('SET_PATH_RESERVATION_REASON_CODE');
export const saveAppStateAndSelectionHandler = makeActionCreator('SAVE_APP_STATE_AND_SELECTION_HANDLER');
export const loadStateAndSelectionHandler = makeActionCreator('LOAD_APP_STATE_AND_SELECTION_HANDLER');
export const setSelectedTimeslot = makeActionCreator('SET_SELECTED_TIMESLOT');
export const unsetSelectedTimeslot = makeActionCreator('UNSET_SELECTED_TIMESLOT');

/**
 *
 * @param dispatch
 * @returns {function(...[*]=)}
 */
const setRootProperties = dispatch => (root) => {
  dispatch(resetBooking()); // clean any previous booking path selections
  dispatch(closeCancelReservationScreen());
  // set the properties of the path into the store
  dispatch(setSelectedPath(root.id));
  dispatch(setUtilizeEmployerInformation(root.utilizeEmployerInformation));
  dispatch(setFetchTimesPermissions(root.canAnonymousFetchTimes));
  if (root.canAnonymousFetchTimes) {
    dispatch(fetchThroughHashedApi)(root.legalEntity);
  }
  dispatch(setCreateUserIfNotExists)(root.createUserIfNotExists);
  dispatch(setRequiresEmployer)(root.requiresEmployer);
  dispatch(setPathReservationByCodeEnabled(root.reservationByCodeEnabled));
  dispatch(setLoginFirst(root.loginFirst));
  if ('loginFirstShowLoginForm' in root) {
    dispatch(setLoginFirstShowLoginForm(root.loginFirstShowLoginForm));
  }
  if ('pathType' in root) {
    dispatch(setSelectedPathType(root.pathType));
  }
  if ('requireSingleEmployer' in root && root.requireSingleEmployer) {
    dispatch(setRequireSingleEmployer(root.requireSingleEmployer));
  }
  // The application should load now that we have set all the variables in place.
  if ('legalEntity' in root) {
    dispatch(setLegalEntity(root.legalEntity));
  }
  if ('reservationReason' in root) {
    dispatch(setPathReservationReasonCode(root.reservationReason));
  }
};

/**
 *
 * @param root
 * @param userIdentified
 * @param user
 * @param selectedEmployer
 * @param employerSelectionEnabled
 * @returns {{promptEmployerSelection: (*|[]|boolean), autoSelectMainEmployer: (boolean|*|{employers}|[])}}
 */
const verifyEmployerAndRoot = (root, userIdentified, user, selectedEmployer, employerSelectionEnabled) => {
  const requiresEmployer = 'requiresEmployer' in root && root.requiresEmployer;
  const utilizeEmployerInformation = 'utilizeEmployerInformation' in root && root.utilizeEmployerInformation;
  const rootNeedsEmployer = requiresEmployer || utilizeEmployerInformation;
  const hasSelectedEmployer = selectedEmployer > -1;
  const hasMultipleEmployers = userIdentified && user && user.employers && user.employers.length > 1;
  const hasOneEmployer = userIdentified && user && user.employers && user.employers.length === 1;
  const rootNeedsEmployerAndHasMultiple = rootNeedsEmployer && hasMultipleEmployers;
  const promptEmployerSelection = rootNeedsEmployerAndHasMultiple && employerSelectionEnabled && !hasSelectedEmployer;
  const autoSelectMainEmployer = rootNeedsEmployer && (!employerSelectionEnabled || hasOneEmployer);
  return {
    rootNeedsEmployer,
    promptEmployerSelection,
    autoSelectMainEmployer
  };
};

/**
 * This action verifies if the root requires employer and if this is the case it proceeds to check what are the configurations
 * either to auto-select an employer, log the user out or prompt the screen to select employer.
 * This action should be dispatched after the user logs in or after the user selects a path while already having a session.
 * This action might trigger a path reload if the organization allows for exclusive paths.
 * @param dispatch
 * @param getState
 * @returns {function(...[*]=)}
 */
export const verifyEmployerForSelectedPath = (dispatch, getState) => () => {
  // we can use getState to verify if the selected path needs an employer
  const { booking, session, organization } = getState();
  let root = null;
  if (booking.path === -1) {
    return; // no path selected
  }
  root = _.find(booking.roots, { id: booking.path });
  // if the path/root requires a single employer but this user has multiple then we should log the user out
  if (root.requireSingleEmployer && session.user.employers.length > 1) {
    dispatch(setIdentityUserFailureDueToMultipleEmployers)(); // This will trigger a logout.
    return; // No need to executing the logic of this function.
  }
  // Otherwise we have to check if we should prompt the user to select an employer.
  const { promptEmployerSelection, autoSelectMainEmployer, rootNeedsEmployer } = verifyEmployerAndRoot(
    root, session.userIdentified, session.user, session.selectedEmployer, organization.employerSelectionEnabled);
  if (promptEmployerSelection) {
    dispatch(openPromptEmployerSelection());
    // prompt the employer selection
    return; // Same here, stop the function execution.
  }
  // auto-select the employer
  if (autoSelectMainEmployer) {
    dispatch(setSelectMainEmployer)(session.user);
  }
  if (!rootNeedsEmployer) {
    /*
    Discussion with Hannu: Do not reset the employer. Users must logout and log back in to change the employer.
     */
    // dispatch(setSelectEmployer)(-1);
  }
};

/**
 *
 * @param dispatch
 * @param getState
 * @returns {function(...[*]=)}
 */
export const dynamicPathLoading = (dispatch, getState) => (root) => {
  const { session } = getState();
  // verify if this path can be fetched without logging in
  if (!session.userIdentified && 'loginBeforePathLoading' in root && root.loginBeforePathLoading) {
    dispatch(setLoginFirst(true));
    dispatch(setFetchPathAfterLogin(root));
    // we have to attach a listener into the login event to load this path
    return;
  } else if ('loginBeforePathLoading' in root && root.loginBeforePathLoading) {
    // if user came via sso (e.g. already identified) we need to keep the state consistent for proper logging out
    dispatch(setFetchPathAfterLogin(root));
  }
  // verify if the user must select an employer
  dispatch(verifyEmployerForSelectedPath)();
  // because an action was dispatched, the store has changed
  // get a new copy of the store before verifying the properties
  const applicationState = getState();
  const updatedSession = {
    ...session,
    ...applicationState.session
  };
  const { booking } = applicationState;

  if (booking.shouldSelectEmployer) {
    return; // The user needs to select the employer before we load the path.
  }
  // set the loading here so that the pumping logo does not blink
  // (gets removed after identifying but then put back on when fetching the path)
  dispatch(pathLoading()); // set the state to "loading the path"
  // If there is a selected employer then add it to the request.
  // This is done to support exclusive paths.
  let employerId = null;
  if (updatedSession.selectedEmployer > -1) {
    const employer = updatedSession.user.employers[updatedSession.selectedEmployer];
    employerId = employer.employer;
  }
  loadPathNamespace(root.pathNamespace, employerId).then(() => {
    dispatch(setPathNamespace(root.pathNamespace));
    // now fetch the path that this root generates
    api.loadPath(root, employerId)
      .then(data => new SelectionHandler(data, root.pathNamespace))
      .then((selectionHandlerInstance) => {
        if (!applicationState.restoredState) {
          dispatch(setSelectionHandler(selectionHandlerInstance));
          return selectionHandlerInstance.config;
        }
        return applicationState.selectionHandler.config;// data
      })
      .then((data) => {
        dispatch(pathLoaded()); // the application should load now that we have set all the variables in place
      })
      .catch((error) => {
        console.error(error);
        dispatch(handleError)(error);
      });
  });
};

/**
 *
 * @param dispatch
 * @param getState
 * @returns {function(...[*]=)}
 */
export const loadPath = (dispatch, getState) => (root) => {
  dispatch(setRootProperties)(root); // set root properties
  dispatch(dynamicPathLoading)(root); // add the dynamic properties
};

/**
 * Verify if a path should be fetched after logging in.
 * @param dispatch
 * @param getState
 * @returns {function(...[*]=)}
 */
export const fetchPathAfterLogin = (dispatch, getState) => () => {
  const { booking } = getState();
  // verify if we should fetch a path after successful login.
  if (booking.fetchPathAfterLogin) {
    dispatch(dynamicPathLoading)(booking.pathToLoadAfterLogin);
  }
};

export const loadRoot = (dispatch, getState) => () => {
  const { booking } = getState();
  if (booking.restoredState) {
    return;
  }
  dispatch(rootLoading());
  api.loadRoot()
    .then((data) => {
      dispatch(rootLoaded(data.roots));
      if (data.roots.length === 1) {
        const root = data.roots[0];
        dispatch(loadPath)(root);
      }
    })
    .catch((error) => { dispatch(handleError)(error) });
};

export const requestTimeSlots = (dispatch, getState) => (params) => {
  dispatch(requestingTimeSlots());
  console.log('Requesting timeslots', params);
  const { legalEntity, canAnonymousFetchTimes, userIdentified } = params;
  api.requestTimeSlots(params)
    .then((data) => {
      console.log('Adding hasResponsible', data);
      const { session } = getState();
      const user = session.user;
      let timeSlotsData = { ...data };
      if (user.employers && user.employers.length) {
        const userEmployer = user.employers[session.selectedEmployer] || {};
        let responsibleResources;
        if (session.hasDepartment) {
          const departments = userEmployer.departments || [];
          const userDepartment = departments[session.selectedDepartment];
          if (userDepartment && userDepartment.resources && userDepartment.resources.length) {
            responsibleResources = userDepartment.resources;
          }
        } else {
          responsibleResources = userEmployer.resources || [];
        }
        responsibleResources = map(responsibleResources, r => r.resource);
        console.log('responsibleResources', responsibleResources);
        // eslint-disable-next-line no-param-reassign
        timeSlotsData = mapValues(data, timeSlots => map(timeSlots, timeSlot => ({
          ...timeSlot,
          hasResponsible: includes(responsibleResources, timeSlot.resource)
        })));
        console.log('Added hasResponsible', timeSlotsData);
      }
      // Before we set the time slots as received we should repopulate the units because depending on
      // whether the path has anonymous access or legal entity the units may be different.
      const getUnitsPromise = !userIdentified || canAnonymousFetchTimes ?
        api.requestUnitsThroughHashedApi(legalEntity) :
        api.requestUnits(legalEntity);
      getUnitsPromise
        .then(localizeUnitName)
        .then((unitData) => {
          dispatch(unitsReceived(unitData));
        })
        .then(() => {
          dispatch(timeSlotsReceived(timeSlotsData));
        });
    })
    .catch((error) => {
      dispatch(handleError)(error);
      dispatch(clearTimeSlots());
    });
};

export const storeReservation = dispatch => (timeSlot, data, selectionPath) => {
  dispatch(storingReservation());
  api.storeReservation(data)
  .then(response => dispatch(reservationStored({ timeSlot, data: response, selectionPath })))
  .then(() => {
    if (data.googleAnalyticsTrackingId && data.selectedPathType) {
      ReactGA.initialize(data.googleAnalyticsTrackingId, {
        titleCase: false,
        gaOptions: {
          allowLinker: true
        }
      });
      ReactGA.event({
        category: 'mBooking',
        action: 'reservationMade',
        label: data.selectedPathType
      });
    }
  })
  .catch((error) => {
    console.error(error);
    dispatch(handleError)(error);
  });
};

export const resetBookingState = dispatch => () => {
  dispatch(resetBooking());
  dispatch(loadRoot)();
};

export const setManagementOption = dispatch => () => {
  dispatch(selectManagementOption());
};

export const setManagementOptionWithSinglePath = dispatch => () => {
  dispatch(selectManagementOptionWithSinglePath());
};

export const unsetManagementOptionWithSinglePath = dispatch => () => {
  dispatch(unselectManagementOptionWithSinglePath());
};

export const setReservationReasonCode = dispatch => (reservationReasonCode) => {
  dispatch(setPathReservationReasonCode(reservationReasonCode));
};

export const saveStateAndSelectionHandler = (dispatch, getState) => () => {
  dispatch(saveAppStateAndSelectionHandler());
  const state = getState();
  if (state.booking.selectionHandler) {
    ls('selectionHandler', JSON.stringify(state.booking.selectionHandler));
  }
  ls('state', JSON.stringify(getState()));
};

export const restoreStateAndSelectionHandler = (dispatch, getState) => () => {
  const { booking } = getState();
  if (booking.savedState) {
    dispatch(loadStateAndSelectionHandler());
    dispatch(setSelectionHandler(new SelectionHandler()));
    ls.remove('state');
    ls.remove('selectionHandler');
  }
};



// WEBPACK FOOTER //
// ./src/actions/booking.js